import getTransactionAccountAndPurseHashes from '../../../utils/get-transaction-account-and-purse-hashes';
import React from 'react';
import { InfoIcon, TableRow, TableRowType } from '@make-software/cspr-design';
import { TableDataHeader } from '@block-explorer/ui';
import { useGetPaginatedAccountDeploys } from '../../../hooks/event-store-hooks';
import {
  BlockHeightFilter,
  ContractPackageHashFilter,
  InfoSvgIcon,
  PageDataTable,
  TabDataTable,
  WASM_DEPLOY,
} from '../../../components';
import { Trans, useTranslation } from 'react-i18next';
import { DeployListItem } from '../../../components/deploy-list-item/deploy-list-item';
import { checkApiHookIsLoading } from '../../../hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { AccountDataProvider } from '../../../services/account-data-service';

export interface AccountDeployListProps {
  publicKey: string;
  pageSize?: number;
  showTopPagination?: boolean;
}

export const AccountDeployList = ({
  publicKey,
  pageSize = 10,
  showTopPagination,
}: AccountDeployListProps) => {
  const { t } = useTranslation();

  const { selectedCurrency: currency } = useSelector(
    (state: RootState) => state.currencies
  );

  const {
    data: deploysData,
    loading: deploysLoading,
    error: deploysError,
    paginationProps,
    sortingProps,
    filterProps,
  } = useGetPaginatedAccountDeploys(
    publicKey
      ? {
          publicKey: publicKey,
          includes: `rate(${currency.id}),contract_entrypoint,contract_package,transfers,centralized_account_info,account_info,cspr_name,nft_token_actions,ft_token_actions,token_market_data(${currency.id})`,
          defaultPageSize: pageSize,
        }
      : null
  );

  const loading = checkApiHookIsLoading({
    loading: deploysLoading,
    error: deploysError,
  });

  const accountAndPurseHashes =
    getTransactionAccountAndPurseHashes(deploysData);
  const TableComponent = showTopPagination ? PageDataTable : TabDataTable;
  const hasNothingFound = !deploysData?.length && !loading;
  return (
    <AccountDataProvider accountAndPurseHashes={accountAndPurseHashes}>
      <TableComponent
        loading={loading}
        data={deploysData}
        error={deploysError}
        noDataError={hasNothingFound}
        paginationProps={paginationProps}
        sortingProps={sortingProps}
        tableRowType={TableRowType.TextWithIcon}
        renderDataHeader={({ sortingProps }) => (
          <TableRow>
            <TableDataHeader fixedWidthRem={15}>
              {t('Transaction Hash')}
            </TableDataHeader>
            <TableDataHeader
              fixedWidthRem={11.875}
              sortableColumnField={'block_height'}
              {...sortingProps}
              filter={<BlockHeightFilter {...filterProps} />}
            >
              <Trans t={t}>Block Height</Trans>
            </TableDataHeader>
            <TableDataHeader
              reverseSortingDirection
              fixedWidthRem={7.69}
              sortableColumnField={'timestamp'}
              {...sortingProps}
            >
              <Trans t={t}>Age</Trans>
            </TableDataHeader>
            <TableDataHeader
              fixedWidthRem={17.5}
              filter={<ContractPackageHashFilter {...filterProps} />}
            >
              <Trans t={t}>Action</Trans>
            </TableDataHeader>
            <TableDataHeader align="right" fixedWidthRem={10.875}>
              <Trans t={t}>Amount</Trans>
            </TableDataHeader>
            <TableDataHeader
              align="right"
              icon={<InfoSvgIcon src={InfoIcon} />}
              tooltipText={t(
                `CSPR tokens spent for executing the transaction. Calculated as the transaction payment minus the partial refund of unused gas. A 75% refund is provided on unused gas for successfully executed non-native transactions.`
              )}
            >
              <Trans t={t}>Charge</Trans>
            </TableDataHeader>
          </TableRow>
        )}
        renderPaginatedData={(deploys) =>
          deploys.map((data, idx) => {
            const isCsprFun = data?.args?.token_to_trade_contract_hash_key;
            return (
              <DeployListItem
                key={idx}
                loading={loading}
                data={data}
                showAccountInfo={false}
                showOnlyEntryPointName={
                  data.executionTypeId === WASM_DEPLOY && !isCsprFun
                }
              />
            );
          })
        }
      />
    </AccountDataProvider>
  );
};

export default AccountDeployList;
